import type {
  AssetProcessingActivitiesMetrics,
  AssetProcessingActivitiesMetricsDTO,
  AssetProcessingActivityList,
  AssetProcessingActivityListDTO,
  AssetProcessingActivityReviewActionOutput,
  AssetProcessingActivityReviewActionOutputDTO,
  NeverPublishedProcessingActivity,
  NeverPublishedProcessingActivityOutputDTO,
  NeverPublishedProcessingActivitySaveInput,
  PatchedNeverPublishedProcessingActivitySaveDTO,
  PatchedSharedDraftSaveDTO,
  ProcessingActivitiesMetrics,
  ProcessingActivitiesMetricsDTO,
  ProcessingActivity,
  ProcessingActivityCategoryWriteInput,
  ProcessingActivityDetail,
  ProcessingActivityDetailDTO,
  ProcessingActivityListDTO,
  ProcessingActivityPurposeWriteInput,
  ProcessingActivityPublishInput,
  ProcessingActivityReview,
  ProcessingActivityStatusDTO,
  ProcessingActivityStructuredWriteDTO,
  ProcessingActivityUpdateInput,
  ProcessingActivityWriteDTO,
  ProcessingPurposeDraftInputDTO,
  ProcessingRoleDTO,
  RopaCategoryRefDTO,
  SharedDraftOutput,
  SharedDraftOutputDTO,
  SharedDraftPublishInput,
  SharedDraftPurposeInputDTO,
  SharedDraftSaveInput,
  SharedDraftSaveResponseDTO,
  SharedDraftWriteDTO,
  SharedDraftWritePayload,
} from './processingActivities.types';

const normalizeProcessingRole = (value: ProcessingRoleDTO) => value ?? '';

const normalizeStatus = (value: ProcessingActivityStatusDTO) => value ?? '';

const parseJsonArray = <T>(value: string | undefined): T[] => {
  if (!value?.trim()) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

const isLegacyWritePayload = (payload: SharedDraftWritePayload): payload is SharedDraftWriteDTO => {
  const candidate = payload as SharedDraftWriteDTO;
  return (
    typeof candidate.purposes === 'string' ||
    typeof candidate.data_subject_categories === 'string' ||
    typeof candidate.personal_data_categories === 'string' ||
    typeof candidate.recipient_categories === 'string'
  );
};

const mapLegacyPurposeToDraftInput = (purpose: Record<string, unknown>): ProcessingPurposeDraftInputDTO => ({
  ...(typeof purpose.id === 'number' ? { id: purpose.id } : {}),
  ...(typeof purpose.purpose_text === 'string' ? { purpose_text: purpose.purpose_text } : {}),
  ...(typeof purpose.legal_basis === 'string' ? { legal_basis: purpose.legal_basis as ProcessingPurposeDraftInputDTO['legal_basis'] } : {}),
  ...(typeof purpose.legal_basis_comment === 'string' ? { legal_basis_comment: purpose.legal_basis_comment } : {}),
  ...(typeof purpose.retention_trigger === 'string' ? { retention_trigger: purpose.retention_trigger } : {}),
  ...(typeof purpose.retention_value === 'number' || purpose.retention_value === null
    ? { retention_value: purpose.retention_value as number | null }
    : {}),
  ...(typeof purpose.retention_unit === 'string'
    ? { retention_unit: purpose.retention_unit as ProcessingPurposeDraftInputDTO['retention_unit'] }
    : {}),
  ...(typeof purpose.retention_comment === 'string' ? { retention_comment: purpose.retention_comment } : {}),
  ...(typeof purpose.retention_description === 'string' && purpose.retention_description
    ? { retention_comment: purpose.retention_description }
    : {}),
  ...(typeof purpose.sort_order === 'number' ? { sort_order: purpose.sort_order } : {}),
  ...(purpose.is_deleted === true || purpose.is_removed === true ? { is_removed: true } : {}),
});

const mapLegacyCategoryToRef = (category: Record<string, unknown>): RopaCategoryRefDTO => {
  if (typeof category.id === 'number') {
    return { id: category.id };
  }

  return { name: typeof category.name === 'string' ? category.name : '' };
};

const mapCategoryWriteInputToRef = (category: ProcessingActivityCategoryWriteInput): RopaCategoryRefDTO => {
  if (category.id != null) {
    return { id: category.id };
  }

  return { name: category.name.trim() };
};

const mapPurposeWriteInputToDraftInput = (purpose: ProcessingActivityPurposeWriteInput): ProcessingPurposeDraftInputDTO => ({
  ...(purpose.id != null ? { id: purpose.id } : {}),
  ...(purpose.purposeText !== undefined ? { purpose_text: purpose.purposeText } : {}),
  ...(purpose.legalBasis ? { legal_basis: purpose.legalBasis } : {}),
  ...(purpose.legalBasisComment !== undefined ? { legal_basis_comment: purpose.legalBasisComment } : {}),
  ...(purpose.retentionTrigger !== undefined ? { retention_trigger: purpose.retentionTrigger } : {}),
  ...(purpose.retentionValue !== undefined ? { retention_value: purpose.retentionValue } : {}),
  ...(purpose.retentionUnit !== undefined ? { retention_unit: purpose.retentionUnit || null } : {}),
  ...(purpose.retentionComment !== undefined ? { retention_comment: purpose.retentionComment } : {}),
  ...(purpose.sortOrder !== undefined ? { sort_order: purpose.sortOrder } : {}),
  ...(purpose.isRemoved ? { is_removed: true } : {}),
});

const mapPurposeWriteInputToSharedDraftInput = (purpose: ProcessingActivityPurposeWriteInput): SharedDraftPurposeInputDTO => ({
  ...mapPurposeWriteInputToDraftInput(purpose),
  ...(purpose.markedForRemoval ? { marked_for_removal: true } : {}),
});

const mapStructuredWriteInputToDto = (input: SharedDraftSaveInput): ProcessingActivityStructuredWriteDTO => ({
  ...(input.name !== undefined ? { name: input.name.trim() } : {}),
  ...(input.description !== undefined ? { description: input.description } : {}),
  ...(input.processingRole !== undefined ? { processing_role: input.processingRole || null } : {}),
  ...(input.minimumSecurityMeasures !== undefined ? { minimum_security_measures: input.minimumSecurityMeasures } : {}),
  ...(input.purposes !== undefined ? { purposes: input.purposes.map(mapPurposeWriteInputToDraftInput) } : {}),
  ...(input.dataSubjectCategories !== undefined
    ? { data_subject_categories: input.dataSubjectCategories.map(mapCategoryWriteInputToRef) }
    : {}),
  ...(input.personalDataCategories !== undefined
    ? { personal_data_categories: input.personalDataCategories.map(mapCategoryWriteInputToRef) }
    : {}),
  ...(input.recipientCategories !== undefined ? { recipient_categories: input.recipientCategories.map(mapCategoryWriteInputToRef) } : {}),
});

const mapLegacyWritePayloadToStructuredDto = (payload: ProcessingActivityWriteDTO): ProcessingActivityStructuredWriteDTO => ({
  ...(payload.name !== undefined ? { name: payload.name } : {}),
  ...(payload.description !== undefined ? { description: payload.description } : {}),
  ...(payload.processing_role !== undefined
    ? { processing_role: (payload.processing_role || null) as ProcessingActivityStructuredWriteDTO['processing_role'] }
    : {}),
  ...(payload.minimum_security_measures !== undefined ? { minimum_security_measures: payload.minimum_security_measures } : {}),
  ...(payload.purposes !== undefined
    ? { purposes: parseJsonArray<Record<string, unknown>>(payload.purposes).map(mapLegacyPurposeToDraftInput) }
    : {}),
  ...(payload.data_subject_categories !== undefined
    ? { data_subject_categories: parseJsonArray<Record<string, unknown>>(payload.data_subject_categories).map(mapLegacyCategoryToRef) }
    : {}),
  ...(payload.personal_data_categories !== undefined
    ? { personal_data_categories: parseJsonArray<Record<string, unknown>>(payload.personal_data_categories).map(mapLegacyCategoryToRef) }
    : {}),
  ...(payload.recipient_categories !== undefined
    ? { recipient_categories: parseJsonArray<Record<string, unknown>>(payload.recipient_categories).map(mapLegacyCategoryToRef) }
    : {}),
});

export const parseReviewForFE = (value: string | null | undefined): ProcessingActivityReview | null => {
  if (!value) return null;

  const slashMatch = value.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (slashMatch) {
    return {
      answered: Number(slashMatch[1]),
      included: Number(slashMatch[2]),
    };
  }

  return null;
};

export const parseProcessingActivityListForFE = (dto: ProcessingActivityListDTO): ProcessingActivity => ({
  id: dto.id,
  name: dto.name,
  processingRole: normalizeProcessingRole(dto.processing_role),
  legalBasis: dto.legal_basis,
  publishedVersionNumber: dto.published_version_number,
  linkedAssetsCount: dto.linked_assets_count,
  review: parseReviewForFE(dto.review),
  dpiaIndicationsCount: dto.dpia_indications_count,
  reviewPriority: dto.review_priority,
  reviewPriorityReasons: dto.review_priority_reasons,
  status: normalizeStatus(dto.status),
  hasDraft: dto.has_draft,
  updatedAt: dto.updated_at,
});

/** @deprecated Use parseProcessingActivityListForFE */
export const parseProcessingActivityForFE = parseProcessingActivityListForFE;

export const parseProcessingActivitiesForFE = (dtos: ProcessingActivityListDTO[]): ProcessingActivity[] =>
  dtos.map(parseProcessingActivityListForFE);

export const parseProcessingActivityDetailForFE = (dto: ProcessingActivityDetailDTO): ProcessingActivityDetail => ({
  id: dto.id,
  name: dto.name,
  description: dto.description ?? '',
  processingRole: normalizeProcessingRole(dto.processing_role),
  minimumSecurityMeasures: dto.minimum_security_measures ?? '',
  publishedVersionNumber: dto.published_version_number,
  publishedUpdatedAt: dto.published_updated_at,
  status: normalizeStatus(dto.status),
  legalBasis: dto.legal_basis,
  hasDraft: dto.has_draft,
  purposes: dto.purposes,
  dataSubjectCategories: dto.data_subject_categories,
  personalDataCategories: dto.personal_data_categories,
  recipientCategories: dto.recipient_categories,
  changelog: dto.changelog,
  assetProcessingActivitiesMetrics: dto.asset_processing_activities_metrics,
});

export const parseNeverPublishedProcessingActivityForFE = (
  dto: NeverPublishedProcessingActivityOutputDTO,
): NeverPublishedProcessingActivity => ({
  id: dto.id,
  name: dto.name,
  description: dto.description ?? '',
  processingRole: normalizeProcessingRole(dto.processing_role),
  minimumSecurityMeasures: dto.minimum_security_measures ?? '',
  publishedVersionNumber: dto.published_version_number,
  status: normalizeStatus(dto.status),
  purposes: dto.purposes,
  dataSubjectCategories: dto.data_subject_categories,
  personalDataCategories: dto.personal_data_categories,
  recipientCategories: dto.recipient_categories,
});

export const parseProcessingActivitiesMetricsForFE = (dto: ProcessingActivitiesMetricsDTO): ProcessingActivitiesMetrics => ({
  publishedProcessingActivitiesCount: dto.published_processing_activities_count,
  assetsAwaitingReviewCount: dto.assets_awaiting_review_count,
  dpiaIndicationsCount: dto.dpia_indications_count,
  draftsInProgressCount: dto.drafts_in_progress_count,
  archivedProcessingActivitiesCount: dto.archived_processing_activities_count,
});

export const parseSharedDraftOutputForFE = (dto: SharedDraftOutputDTO | SharedDraftSaveResponseDTO): SharedDraftOutput => ({
  processingActivityId: dto.processing_activity_id,
  draftRevision: dto.draft_revision,
  basePublishedVersionNumber: dto.base_published_version_number,
  name: dto.name,
  description: dto.description ?? '',
  processingRole: normalizeProcessingRole(dto.processing_role),
  minimumSecurityMeasures: dto.minimum_security_measures ?? '',
  changeComment: dto.change_comment ?? '',
  updatedByMembershipId: dto.updated_by_membership_id,
  updatedByDisplayName: dto.updated_by_display_name,
  updatedAt: dto.updated_at,
  purposes: dto.purposes,
  dataSubjectCategories: dto.data_subject_categories,
  personalDataCategories: dto.personal_data_categories,
  recipientCategories: dto.recipient_categories,
});

export const parseNeverPublishedProcessingActivitySaveForBE = (
  input: NeverPublishedProcessingActivitySaveInput,
): PatchedNeverPublishedProcessingActivitySaveDTO => mapStructuredWriteInputToDto(input);

export const parseSharedDraftSaveForBE = (input: SharedDraftSaveInput): PatchedSharedDraftSaveDTO => {
  const structured = mapStructuredWriteInputToDto(input);

  return {
    ...structured,
    ...(input.draftRevision != null ? { draft_revision: input.draftRevision } : {}),
    ...(input.changeComment !== undefined ? { change_comment: input.changeComment } : {}),
    ...(input.purposes !== undefined ? { purposes: input.purposes.map(mapPurposeWriteInputToSharedDraftInput) } : {}),
  };
};

export const parseSharedDraftWritePayloadForBE = (payload: SharedDraftWritePayload): PatchedSharedDraftSaveDTO => {
  if (!isLegacyWritePayload(payload)) {
    return parseSharedDraftSaveForBE(payload);
  }

  const structured = mapLegacyWritePayloadToStructuredDto(payload);

  return {
    ...structured,
    ...(payload.draft_revision != null ? { draft_revision: payload.draft_revision } : {}),
    ...(payload.change_comment !== undefined ? { change_comment: payload.change_comment } : {}),
    ...(structured.purposes !== undefined
      ? {
          purposes: structured.purposes.map(purpose => ({
            ...purpose,
            ...(purpose.is_removed ? { marked_for_removal: true } : {}),
          })),
        }
      : {}),
  };
};

export const isSharedDraftPublishInput = (payload: ProcessingActivityPublishInput): payload is SharedDraftPublishInput =>
  'draftRevision' in payload && 'changeComment' in payload;

export const parseProcessingActivityPublishForBE = (input: ProcessingActivityPublishInput): Record<string, unknown> => {
  if (isSharedDraftPublishInput(input)) {
    return {
      draft_revision: input.draftRevision,
      change_comment: input.changeComment,
    };
  }

  return parseNeverPublishedProcessingActivitySaveForBE(input);
};

export const parseSharedDraftPublishForBE = (input: SharedDraftPublishInput) => ({
  draft_revision: input.draftRevision,
  change_comment: input.changeComment,
});

/** @deprecated Use parseNeverPublishedProcessingActivitySaveForBE */
export const parseProcessingActivityUpdateForBE = (
  input: ProcessingActivityUpdateInput,
): PatchedNeverPublishedProcessingActivitySaveDTO => ({
  ...(input.name !== undefined ? { name: input.name } : {}),
  ...(input.description !== undefined ? { description: input.description } : {}),
  ...(input.processingRole !== undefined ? { processing_role: input.processingRole || null } : {}),
  ...(input.minimumSecurityMeasures !== undefined ? { minimum_security_measures: input.minimumSecurityMeasures } : {}),
});

export const parseRopaCategoriesForFE = (dtos: { id: number; name: string }[]) =>
  dtos.map(dto => ({
    id: dto.id,
    name: dto.name,
  }));

export const parseAssetProcessingActivityListForFE = (dto: AssetProcessingActivityListDTO): AssetProcessingActivityList => ({
  id: dto.id,
  assetId: dto.asset_id,
  assetName: dto.asset_name,
  operationalOwnerMembershipId: dto.operational_owner_membership_id,
  operationalOwnerDisplayName: dto.operational_owner_display_name,
  officialReviewStatus: dto.official_review_status,
  updatedAt: dto.updated_at,
  dpiaStatus: dto.dpia_status,
  activePurposesCount: dto.active_purposes_count,
  hasSensitivePersonalData: dto.has_sensitive_personal_data,
});

export const parseAssetProcessingActivitiesForFE = (dtos: AssetProcessingActivityListDTO[]): AssetProcessingActivityList[] =>
  dtos.map(parseAssetProcessingActivityListForFE);

export const parseAssetProcessingActivitiesMetricsForFE = (dto: AssetProcessingActivitiesMetricsDTO): AssetProcessingActivitiesMetrics => ({
  linkedAssetProcessingsCount: dto.linked_asset_processings_count,
  awaitingResponseCount: dto.awaiting_response_count,
  submittedCount: dto.submitted_count,
  acceptedCount: dto.accepted_count,
  dpiaIndicatedCount: dto.dpia_indicated_count,
  sensitivePersonalDataCount: dto.sensitive_personal_data_count,
});

export const parseAssetProcessingActivityReviewActionOutputForFE = (
  dto: AssetProcessingActivityReviewActionOutputDTO,
): AssetProcessingActivityReviewActionOutput => ({
  id: dto.id,
  status: dto.status,
  acceptedAt: dto.accepted_at,
  acceptedByMembershipId: dto.accepted_by_membership_id,
  clarificationComment: dto.clarification_comment,
  clarificationRequestedAt: dto.clarification_requested_at,
  clarificationRequestedByMembershipId: dto.clarification_requested_by_membership_id,
});

const isIgnorableValidationMessage = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return !normalized || normalized === 'none' || normalized === 'null' || normalized === 'undefined';
};

const formatValidationPath = (path: string[]): string => {
  const labels: string[] = [];

  path.forEach((segment, index) => {
    const previous = path[index - 1];

    if (/^\d+$/.test(segment)) {
      labels.push(previous === 'purposes' ? `Purpose ${Number(segment) + 1}` : String(Number(segment) + 1));
      return;
    }

    if (segment === 'purposes' && /^\d+$/.test(path[index + 1] ?? '')) {
      return;
    }

    if (segment === 'detail') {
      return;
    }

    labels.push(segment.replace(/_/g, ' '));
  });

  return labels.join(', ');
};

const extractValidationMessages = (data: unknown, includePaths: boolean): string[] => {
  const messages: string[] = [];
  const seen = new Set<string>();

  const visit = (node: unknown, path: string[]): void => {
    if (node == null) {
      return;
    }

    if (typeof node === 'string') {
      const trimmed = node.trim();
      if (isIgnorableValidationMessage(trimmed)) {
        return;
      }

      const location = includePaths ? formatValidationPath(path) : '';
      const formatted = location ? `${location}: ${trimmed}` : trimmed;
      if (seen.has(formatted)) {
        return;
      }
      seen.add(formatted);
      messages.push(formatted);
      return;
    }

    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        const nextPath = typeof item === 'object' && item != null ? [...path, String(index)] : path;
        visit(item, nextPath);
      });
      return;
    }

    if (typeof node === 'object') {
      Object.entries(node as Record<string, unknown>).forEach(([key, value]) => visit(value, [...path, key]));
    }
  };

  visit(data, []);
  return messages;
};

/**
 * Walks DRF-style validation error payloads (nested objects/arrays) and returns
 * unique human-readable messages. Ignores positional null placeholders such as `"None"`.
 */
export const extractUniqueValidationMessages = (data: unknown): string[] => extractValidationMessages(data, false);

/**
 * Same as extractUniqueValidationMessages, but prefixes nested errors with the
 * purpose index and field name so users can locate the failing field.
 */
export const extractContextualValidationMessages = (data: unknown): string[] => extractValidationMessages(data, true);
